import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Clock, TrendingUp, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * ActionCard — the "AI recommendation" pattern.
 * Shows: title, Why it matters, Impact/Time/Salary/Confidence chips, single CTA.
 * Avoids walls of text.
 */
export default function ActionCard({
  title,
  reason,
  impact,
  timeRequired,
  salaryImpact,
  confidence,
  ctaLabel = "Take action",
  onAction,
  icon: Icon = Sparkles,
  testId,
  variant = "default",
}) {
  const variants = {
    default: "border-border bg-card",
    primary: "border-primary/40 bg-primary/5",
    success: "border-[hsl(var(--success))/0.4] bg-[hsl(var(--success))/0.05]",
    warning: "border-[hsl(var(--warning))/0.4] bg-[hsl(var(--warning))/0.05]",
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative border-l-4 border-primary rounded-xl border ${variants[variant]} p-5 card-hover overflow-hidden`}
      data-testid={testId}
    >
      <div className="flex items-start gap-4">
        <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Icon size={16} strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-bold text-base md:text-lg tracking-tight text-foreground mb-1">
            {title}
          </h3>
          {reason && (
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">{reason}</p>
          )}

          {(impact || timeRequired || salaryImpact || confidence) && (
            <div className="flex flex-wrap gap-2 mb-4">
              {impact && (
                <Chip icon={TrendingUp} label={impact} tone="primary" />
              )}
              {timeRequired && (
                <Chip icon={Clock} label={timeRequired} tone="muted" />
              )}
              {salaryImpact && (
                <Chip icon={Sparkles} label={salaryImpact} tone="success" />
              )}
              {confidence && (
                <Chip icon={Zap} label={confidence} tone="warning" />
              )}
            </div>
          )}

          {onAction && (
            <Button size="sm" onClick={onAction} data-testid={`${testId || "action-card"}-cta`} className="gap-2">
              {ctaLabel} <ArrowRight size={14} />
            </Button>
          )}
        </div>
      </div>
    </motion.article>
  );
}

function Chip({ icon: Icon, label, tone = "muted" }) {
  const tones = {
    primary: "bg-primary/10 text-primary",
    success: "bg-[hsl(var(--success))/0.12] text-[hsl(var(--success))]",
    warning: "bg-[hsl(var(--warning))/0.12] text-[hsl(var(--warning))]",
    muted: "bg-secondary text-secondary-foreground",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-mono font-medium ${tones[tone]}`}>
      <Icon size={11} />
      {label}
    </span>
  );
}
